import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getImagePosts,
  createImagePost,
  updateImagePost,
  deleteImagePost,
  getImageComments,
  likeImage,
  commentImage,
  editImageComment,
  deleteImageComment,
} from "../../services/api";
import CommentForm from "../posts/CommentForm";
import CommentList from "../posts/CommentList";
import { Pencil, Trash2, Camera, Images, Heart, MessageCircle } from "lucide-react";
import { optimizeImage } from "../../utils/optimizeImage";

const ImageGallery = () => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [imageStats, setImageStats] = useState({});
  const [newImage, setNewImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [editingImage, setEditingImage] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [showDeleteSnackbar, setShowDeleteSnackbar] = useState(false);
  const [showEditSnackbar, setShowEditSnackbar] = useState(false);
  const [showCreateSnackbar, setShowCreateSnackbar] = useState(false);

  const fetchImages = async () => {
    try {
      const res = await getImagePosts();
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setImages(data);

      // La API de imágenes no garantiza el total de comentarios.
      // Consultamos los datos de cada fotografía para mostrar
      // los contadores reales sin modificar Django.
      const resultados = await Promise.allSettled(
        data.map((img) => getImageComments(img.id))
      );

      const estadisticas = {};
      resultados.forEach((resultado, index) => {
        if (resultado.status === "fulfilled") {
          const detalle = resultado.value.data;
          estadisticas[data[index].id] = {
            likes: detalle.likes || 0,
            comments: (detalle.comments || []).length,
          };
        }
      });

      setImageStats(estadisticas);
    } catch (err) {
      console.error(t("image_gallery.load_error"), err);
      setImages([]);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  useEffect(() => {
    const loadImageDetails = async () => {
      if (modalImage) {
        try {
          const res = await getImageComments(modalImage.id);
          const formattedComments = (res.data.comments || []).map((c) => ({
            ...c,
            author_name: `${c.profile_nombre ?? ""} ${c.profile_apellidos ?? ""}`.trim(),
          }));
          setLikes(res.data.likes || 0);
          setLiked(res.data.liked_by_user || false);
          setComments(formattedComments);
          setImageStats((prev) => ({
            ...prev,
            [modalImage.id]: {
              likes: res.data.likes || 0,
              comments: formattedComments.length,
            },
          }));
          setShowComments(false);
        } catch (err) {
          console.error(t("image_gallery.details_error"), err);
        }
      }
    };
    loadImageDetails();
  }, [modalImage]);

  const openFormModal = () => setShowFormModal(true);
  const closeFormModal = () => {
    resetForm();
    setShowFormModal(false);
  };

  const handleLike = async () => {
    try {
      const res = await likeImage(modalImage.id);
      setLikes(res.data.total_likes);
      setLiked(res.data.liked);
      setImageStats((prev) => ({
        ...prev,
        [modalImage.id]: {
          likes: res.data.total_likes,
          comments: prev[modalImage.id]?.comments ?? comments.length,
        },
      }));
    } catch (err) {
      console.error(t("image_gallery.like_error"), err);
    }
  };

  const handleNewComment = async (text) => {
    try {
      const res = await commentImage(modalImage.id, text);
      const newComment = {
        ...res.data,
        author_name: `${res.data.profile_nombre ?? ""} ${res.data.profile_apellidos ?? ""}`.trim(),
      };
      setComments((prev) => [...prev, newComment]);
      setImageStats((prev) => ({
        ...prev,
        [modalImage.id]: {
          likes: prev[modalImage.id]?.likes ?? likes,
          comments: (prev[modalImage.id]?.comments ?? comments.length) + 1,
        },
      }));
    } catch (err) {
      console.error(t("image_gallery.comment_error"), err);
    }
  };

  const handleImageCommentUpdated = (updatedComment) => {
    setComments((prev) =>
      prev.map((comment) =>
        comment.id === updatedComment.id
          ? {
              ...comment,
              ...updatedComment,
              author_name:
                updatedComment.author_name || comment.author_name,
            }
          : comment
      )
    );
  };

  const handleImageCommentDeleted = (commentId) => {
    setComments((prev) => prev.filter((c) => c.id !== commentId));
    setImageStats((prev) => ({
      ...prev,
      [modalImage.id]: {
        likes: prev[modalImage.id]?.likes ?? likes,
        comments: Math.max(
          0,
          (prev[modalImage.id]?.comments ?? comments.length) - 1
        ),
      },
    }));
  };

  const handleEdit = (img) => {
    setEditingImage(img);
    setTitle(img.title);
    setDescription(img.description);
    setPreviewUrl(img.image_url);
    setNewImage(null);
    setShowFormModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    if (newImage) formData.append("image", newImage);

    try {
        if (editingImage) {
          await updateImagePost(editingImage.id, formData);
          setShowEditSnackbar(true);
          setTimeout(() => setShowEditSnackbar(false), 3000);
        } else {
          await createImagePost(formData);
          setShowCreateSnackbar(true);
          setTimeout(() => setShowCreateSnackbar(false), 3000);
        }
      closeFormModal();
      fetchImages();
    } catch (err) {
      console.error(t("image_gallery.save_error"), err);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (img) => {
    setImageToDelete(img);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setImageToDelete(null);
    setShowDeleteModal(false);
  };

  const handleDeleteConfirmed = async () => {
    if (!imageToDelete) return;
    try {
      await deleteImagePost(imageToDelete.id);
      fetchImages();
      setShowDeleteSnackbar(true);
      setTimeout(() => setShowDeleteSnackbar(false), 3000);
    } catch (err) {
      console.error(t("image_gallery.delete_error"), err);
    } finally {
      setShowDeleteModal(false);
      setImageToDelete(null);
    }
  };

  const resetForm = () => {
    setEditingImage(null);
    setTitle("");
    setDescription("");
    setNewImage(null);
    setPreviewUrl(null);
    setSubmitting(false);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setNewImage(null);
    setPreviewUrl(null);

    try {
      const optimized = await optimizeImage(file);
      setNewImage(optimized);
      setPreviewUrl(URL.createObjectURL(optimized));
    } catch (err) {
      console.error("Error al optimizar imagen de galería", err);
      window.alert(
        t("image_gallery.image_error", "No se pudo procesar la fotografía.")
      );
      e.target.value = "";
    }
  };

  // HAPPYCAP_GALLERY_MODERN_CARDS_V1
  return (
    <div className="mx-auto mt-6 max-w-6xl px-4 pb-10 sm:px-6">

      {/* Botón circular para añadir fotografías */}
      <div className="mb-5 flex justify-end">
        <button
          type="button"
          onClick={openFormModal}
          title={t("image_gallery.upload_button")}
          aria-label={t("image_gallery.upload_button")}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D8E7F0] bg-white text-[#247BA8] shadow-sm transition hover:bg-[#EAF3FB] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#247BA8]"
        >
          <Camera size={21} strokeWidth={1.8} aria-hidden="true" />
        </button>
      </div>

      {/* Fotografías */}
      {images.length === 0 ? (
        <div className="rounded-3xl border border-[#E8EDF3] bg-white px-6 py-14 text-center shadow-sm">
          <Images
            size={44}
            className="mx-auto mb-4 text-[#A8CEE4]"
            aria-hidden="true"
          />
          <p className="text-base text-[#45647C]">
            {t("image_gallery.no_images")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {images.map((img) => (
            <article
              key={img.id}
              className="group overflow-hidden rounded-[24px] border border-[#E8EDF3] bg-white shadow-[0_8px_28px_rgba(22,63,99,0.07)] transition-shadow duration-200 hover:shadow-[0_14px_36px_rgba(22,63,99,0.13)]"
            >
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setModalImage(img)}
                  className="block w-full overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#247BA8]"
                  aria-label={img.title || t("image_gallery.select")}
                >
                  <img
                    src={img.image_url}
                    alt={img.title || ""}
                    loading="lazy"
                    className="h-56 w-full bg-slate-50 object-cover transition-transform duration-300 group-hover:scale-[1.025] sm:h-60"
                  />
                </button>

                <div className="absolute right-3 top-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(img)}
                    title={t("image_gallery.edit", "Editar")}
                    aria-label={t("image_gallery.edit", "Editar")}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#247BA8] shadow-md transition hover:bg-[#EAF3FB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#247BA8]"
                  >
                    <Pencil size={18} />
                  </button>

                  <button
                    type="button"
                    onClick={() => confirmDelete(img)}
                    title={t("image_gallery.delete", "Eliminar")}
                    aria-label={t("image_gallery.delete", "Eliminar")}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-red-500 shadow-md transition hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="px-5 pb-4 pt-4">
                {img.title && (
                  <h2 className="line-clamp-1 text-lg font-bold text-[#163F63]">
                    {img.title}
                  </h2>
                )}

                {img.description && (
                  <p className="mt-2 line-clamp-2 text-sm leading-6 text-[#45647C]">
                    {img.description}
                  </p>
                )}

                <div className="mt-4 flex items-center justify-start gap-5 border-t border-slate-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setModalImage(img)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#245580] transition hover:text-[#247BA8]"
                    aria-label="Ver me gusta"
                  >
                    <Heart size={19} className="text-[#DF5684]" />
                    {imageStats[img.id]?.likes ?? "…"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setModalImage(img)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#245580] transition hover:text-[#247BA8]"
                    aria-label="Ver comentarios"
                  >
                    <MessageCircle size={19} className="text-[#247BA8]" />
                    {imageStats[img.id]?.comments ?? "…"}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {showFormModal && (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/65 px-4 py-8 sm:py-12"          onClick={closeFormModal}
        >
          <div
            className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeFormModal}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-lg text-slate-500 transition hover:bg-slate-200"
            >
              ✖
            </button>

            <h3 className="mb-6 pr-10 text-xl font-bold text-[#163F63]">
              {editingImage ? t("image_gallery.edit_image") : t("image_gallery.new_image")}
            </h3>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <input
                  type="text"
                  placeholder={t("image_gallery.title_placeholder", "Título")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-[#163F63] outline-none transition focus:border-[#247BA8] focus:bg-white"
                />
                <textarea
                  placeholder={t("image_gallery.description_placeholder", "Descripción")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-[#163F63] outline-none transition focus:border-[#247BA8] focus:bg-white"
                />
                <div className="flex justify-center">
                  <label htmlFor="gallery-image-upload" className="cursor-pointer">
                    <span
                      className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D8E7F0] bg-white text-[#247BA8] shadow-sm transition hover:bg-[#EAF3FB] hover:shadow-md"
                      title={t("image_gallery.select")}
                      aria-hidden="true"
                    >
                      <Camera size={22} strokeWidth={1.8} />
                    </span>
                  </label>
                  <input
                    id="gallery-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </div>
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Vista previa"
                  className="h-56 w-full rounded-2xl border border-slate-100 bg-slate-50 object-contain"
                />
              )}
              <button
                type="submit"
                disabled={submitting}
                className="mx-auto mt-2 inline-flex min-h-10 items-center justify-center rounded-full bg-[#247BA8] px-7 py-2 text-sm font-medium text-white transition hover:bg-[#19658E] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting
                  ? t("image_gallery.saving")
                  : editingImage
                  ? t("image_gallery.save_changes")
                  : t("image_gallery.submit")}
              </button>
            </form>
          </div>
        </div>
      )}

      {modalImage && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/75 px-3 py-8 sm:py-12"
          onClick={() => setModalImage(null)}
        >
          <div
            className="relative w-full max-w-2xl rounded-3xl bg-white p-5 shadow-2xl sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setModalImage(null)}
              aria-label={t("common.close", "Cerrar")}
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-500 shadow transition hover:bg-slate-100"
            >
              ✕
            </button>

            <img
              src={modalImage.image_url}
              alt={modalImage.title || ""}
              className="max-h-[65vh] w-full rounded-2xl bg-slate-50 object-contain"
            />

            {modalImage.title && (
              <h3 className="mt-5 text-lg font-bold text-[#245580]">
                {modalImage.title}
              </h3>
            )}

            {modalImage.description && (
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {modalImage.description}
              </p>
            )}

            <div className="mt-5 flex justify-center gap-4 border-t border-slate-100 pt-5">
              <button
                type="button"
                onClick={handleLike}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[#D8E7F0] bg-white px-5 py-2 text-sm font-medium text-[#245580] transition hover:bg-[#F2F8FC]"
              >
                {liked ? "💖" : "❤️"} {likes}
              </button>

              <button
                type="button"
                onClick={() => setShowComments((prev) => !prev)}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[#D8E7F0] bg-white px-5 py-2 text-sm font-medium text-[#245580] transition hover:bg-[#F2F8FC]"
              >
                💬 {comments.length}
              </button>
            </div>

            {showComments && (
              <div className="gallery-comments-modern mt-5 rounded-2xl bg-[#F5F9FC] p-4 sm:p-5">
                <CommentForm onSubmit={handleNewComment} />
                <CommentList
                  comments={comments}
                  onCommentUpdated={handleImageCommentUpdated}
                  onCommentDeleted={handleImageCommentDeleted}
                  editCommentAction={editImageComment}
                  deleteCommentAction={deleteImageComment}
                />
              </div>
            )}
          </div>
        </div>
      )}


      {/* HAPPYCAP_GALLERY_COMMENTS_V2 */}
      <style>{`
        .gallery-comments-modern form {
          gap: 10px !important;
          align-items: stretch !important;
        }

        .gallery-comments-modern form input[type="text"] {
          border: 1px solid #d8e7f0 !important;
          border-radius: 14px !important;
          background: #ffffff !important;
          padding: 12px 14px !important;
          font-size: 14px !important;
          outline: none !important;
        }

        .gallery-comments-modern form input[type="text"]:focus {
          border-color: #247ba8 !important;
          box-shadow: 0 0 0 2px rgba(36, 123, 168, 0.12);
        }

        .gallery-comments-modern form button[type="submit"] {
          align-self: flex-end !important;
          border-radius: 999px !important;
          background: #247ba8 !important;
          padding: 9px 20px !important;
          font-size: 13px !important;
          font-weight: 500 !important;
        }

        .gallery-comments-modern form button[type="submit"]:hover:not(:disabled) {
          background: #19658e !important;
        }

        .gallery-comments-modern form button[type="submit"]:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      {showDeleteModal && (
        <div
            className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/60 px-4 py-6"          onClick={cancelDelete}
        >
          <div
            className="w-full max-w-sm max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">{t("image_gallery.confirm_delete_title")}</h3>
            <p className="mb-6 text-gray-600">{t("image_gallery.confirm_delete_text")}</p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={handleDeleteConfirmed}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
              >
                {t("image_gallery.confirm")}
              </button>
              <button
                onClick={cancelDelete}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400 transition"
              >
                {t("image_gallery.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

        {showDeleteSnackbar && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-5 py-3 rounded-lg shadow-lg text-sm z-50 whitespace-nowrap w-max max-w-[calc(100vw-1rem)]">
            {t("image_gallery.deleted_success")}
          </div>
        )}


        {showEditSnackbar && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-5 py-3 rounded-lg shadow-lg text-sm z-50">
            {t("image_gallery.edited_success")}
          </div>
        )}

        {showCreateSnackbar && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-5 py-3 rounded-lg shadow-lg text-sm z-50">
            {t("image_gallery.created_success")}
          </div>
        )}
    </div>
  );
};

export default ImageGallery;
